
import React from "react";
import { User } from "@supabase/supabase-js";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

interface UserProfileCardProps {
  user: User | null;
}

const UserProfileCard = ({ user }: UserProfileCardProps) => {
  if (!user) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <Skeleton className="h-7 w-[150px] mb-2" />
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-[200px]" />
              <Skeleton className="h-4 w-[150px]" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const initials = user.user_metadata?.first_name?.[0] || user.user_metadata?.last_name?.[0] || user.email?.[0]?.toUpperCase() || '?';
  const fullName = `${user.user_metadata?.first_name || ''} ${user.user_metadata?.last_name || ''}`.trim() || 'User';
  const businessName = user.user_metadata?.business_name || user.user_metadata?.companyName || 'Personal Account';
  
  return (
    <Card>
      <CardHeader className="pb-2">
        <h3 className="text-lg font-semibold">Your Profile</h3>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-4">
          <Avatar className="h-10 w-10">
            <AvatarImage src={user.user_metadata?.avatar_url} />
            <AvatarFallback className="bg-primary text-primary-foreground">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium">{fullName}</p>
            <p className="text-sm text-muted-foreground">{businessName}</p>
          </div>
        </div>
        
        <div className="space-y-1">
          <p className="text-sm"><span className="font-medium">Email:</span> {user.email}</p>
          {user.user_metadata?.phoneNumber && (
            <p className="text-sm"><span className="font-medium">Phone:</span> {user.user_metadata?.phoneNumber}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default UserProfileCard;
